// src/components/TriggerFlowModal/index.js

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  CircularProgress,
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import api from "../../services/api";
import useDialogStyles from "../TransferTicketModalCustom/dialogStyles";

const selectMenuProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  getContentAnchorEl: null,
};

const TriggerFlowModal = ({ 
  open, 
  onClose, 
  ticketId, 
  ticketStatus,
  onFlowTriggered,
  onFlowProcessing
}) => {
  const classes = useDialogStyles({ width: 440 });
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingFlows, setLoadingFlows] = useState(true);

  // Carregar fluxos disponíveis
  useEffect(() => {
    const fetchFlows = async () => {
      if (!open) return;
      
      setLoadingFlows(true);
      try {
        const response = await api.get("/flowbuilder");
        
        // A resposta tem a estrutura: { flows: [...] }
        const flowsData = response.data.flows || [];
        
        console.log("Resposta da API flowbuilder:", flowsData);
        
        // Filtrar apenas fluxos ativos
        const activeFlows = flowsData.filter(flow => flow.active === true);
        setFlows(activeFlows);
        
        console.log("Fluxos ativos encontrados:", activeFlows);
      } catch (error) {
        console.error("Erro ao carregar fluxos:", error);
        toastError("Erro ao carregar fluxos disponíveis");
      } finally {
        setLoadingFlows(false);
      }
    };

    fetchFlows();
  }, [open]);

  // Reset ao abrir/fechar modal
  useEffect(() => {
    if (open) {
      setSelectedFlow("");
    }
  }, [open]);

  // ✅ NOVO: Informar o componente pai sobre o estado de processamento
  useEffect(() => {
    if (onFlowProcessing) {
      onFlowProcessing(loading);
    }
  }, [loading, onFlowProcessing]);

  const handleFlowChange = (event) => {
    setSelectedFlow(event.target.value);
  };

  const handleTriggerFlow = async () => {
    if (!selectedFlow) {
      toast.warning("Selecione um fluxo para disparar");
      return;
    }

    setLoading(true);
    try {
      console.log("🎯 Disparando fluxo:", selectedFlow, "para ticket:", ticketId);
      
      const response = await api.post(`/tickets/${ticketId}/trigger-flow`, {
        flowId: selectedFlow
      });

      if (response.data.success) {
        toast.success("Fluxo disparado com sucesso!");
        
        console.log("✅ Fluxo disparado com sucesso:", response.data.data);
        
        // Callback para notificar o componente pai
        if (onFlowTriggered) {
          onFlowTriggered(response.data.data);
        }
        
        onClose();
      }
    } catch (error) {
      console.error("❌ Erro ao disparar fluxo:", error);
      const errorMessage = error.response?.data?.error || "Erro ao disparar fluxo";
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const getSelectedFlowInfo = () => {
    if (!selectedFlow) return null;
    return flows.find(flow => flow.id === selectedFlow);
  };

  const selectedFlowInfo = getSelectedFlowInfo();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.paper }}
      aria-labelledby="trigger-flow-title"
    >
      <div className={classes.header}>
        <h2 id="trigger-flow-title" className={classes.title}>
          {i18n.t("triggerFlowModal.title")}
        </h2>
        {!loadingFlows && flows.length > 0 && (
          <p className={classes.subtitle}>
            {i18n.t("triggerFlowModal.description")}
          </p>
        )}
      </div>

      <DialogContent className={`${classes.content} ${classes.contentVisible}`}>
        {loadingFlows ? (
          <div className={classes.loading}>
            <CircularProgress size={18} color="inherit" />
          </div>
        ) : flows.length === 0 ? (
          <div className={classes.empty}>
            {i18n.t("triggerFlowModal.noFlows")}
          </div>
        ) : (
          <>
            <div className={classes.field}>
              <span className={classes.label} id="flow-select-label">
                {i18n.t("triggerFlowModal.selectFlow")}
              </span>
              <Select
                fullWidth
                displayEmpty
                variant="outlined"
                margin="dense"
                labelId="flow-select-label"
                value={selectedFlow}
                onChange={handleFlowChange}
                disabled={loading}
                MenuProps={selectMenuProps}
                renderValue={(value) => {
                  const flow = flows.find((f) => f.id === value);
                  if (!flow) {
                    return (
                      <span className={classes.placeholder}>
                        {i18n.t("triggerFlowModal.selectFlow")}
                      </span>
                    );
                  }
                  return flow.name;
                }}
              >
                {flows.map((flow) => (
                  <MenuItem dense key={flow.id} value={flow.id}>
                    {flow.name}
                  </MenuItem>
                ))}
              </Select>
            </div>

            {selectedFlowInfo && (
              <div className={classes.infoBox}>
                <div>
                  <strong>{i18n.t("triggerFlowModal.selectedFlow")}:</strong> {selectedFlowInfo.name}
                </div>
                <div>
                  <strong>{i18n.t("triggerFlowModal.flowId")}:</strong> {selectedFlowInfo.id}
                </div>
              </div>
            )}

            {/* Indicador visual enquanto o fluxo é disparado */}
            {loading && (
              <div className={classes.infoBox} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <CircularProgress size={18} color="inherit" style={{ flex: "none" }} />
                <span>Disparando fluxo... Campo de mensagem temporariamente desabilitado.</span>
              </div>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          onClick={handleClose}
          disabled={loading}
          className={classes.cancel}
        >
          {i18n.t("triggerFlowModal.cancel")}
        </Button>
        <Button
          onClick={handleTriggerFlow}
          color="primary"
          variant="contained"
          disabled={loading || !selectedFlow || loadingFlows || flows.length === 0}
          startIcon={loading && <CircularProgress size={18} color="inherit" />}
          className={classes.confirm}
        >
          {loading
            ? i18n.t("triggerFlowModal.triggering")
            : i18n.t("triggerFlowModal.trigger")
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TriggerFlowModal;
